// Custom Element: carousel-navigation
class CarouselNavigation extends HTMLElement {
    constructor() {
        super();
        this.abortController = null;
        this.allItems = [];
    }

    connectedCallback() {
        if (!this.carousel) {
            console.warn("Carousel navigation component requires an aria-controls attribute that refers to the controlled carousel.");
            return;
        }

        this.abortController = new AbortController();
        this.allItems = Array.from(this.querySelectorAll("button"));

        // Add click listeners to all buttons
        this.allItems.forEach((button, index) => {
            button.addEventListener("click", () => this.onButtonClicked(index), {
                signal: this.abortController.signal
            });
        });

        // Listen to carousel changes
        this.carousel.addEventListener("carousel:change", event => {
            this.onNavigationChange(event.detail.index);
        }, { signal: this.abortController.signal });
    }

    disconnectedCallback() {
        this.abortController?.abort();
    }

    get items() {
        return this.allItems.filter(item => !item.hasAttribute("hidden"));
    }

    get carousel() {
        return document.getElementById(this.getAttribute("aria-controls"));
    }

    get selectedIndex() {
        return this.items.findIndex(button => button.getAttribute("aria-current") === "true");
    }

    onButtonClicked(newIndex) {
        this.carousel.select(newIndex);
        this.onNavigationChange(newIndex);
    }

    onNavigationChange(newIndex) {
        // Update aria-current on all buttons
        this.items.forEach((button, index) => {
            button.setAttribute("aria-current", newIndex === index ? "true" : "false");
        });

        // Scroll the selected button into view if align-selected attribute is present
        if (this.hasAttribute("align-selected") &&
            (this.scrollWidth !== this.clientWidth || this.scrollHeight !== this.clientHeight)) {
            this.scrollTo({
                left: this.items[newIndex].offsetLeft - this.clientWidth / 2 + this.items[newIndex].clientWidth / 2,
                top: this.items[newIndex].offsetTop - this.clientHeight / 2 + this.items[newIndex].clientHeight / 2,
                behavior: "smooth"
            });
        }
    }
}

// Custom Element: featured-collections-carousel
class FeaturedCollectionsCarousel extends HTMLElement {
    constructor() {
        super();
        this.targetIndex = 0;
    }

    connectedCallback() {
        this.targetIndex = Math.max(0, this.items.findIndex(item => item.classList.contains("is-selected")));
        
        // Ensure first item is selected if none are
        if (this.targetIndex === -1 && this.items.length > 0) {
            this.targetIndex = 0;
            this.items[0].classList.add("is-selected");
        }
    }

    get items() {
        return Array.from(this.querySelectorAll(".featured-collections-carousel__item")).filter(
            item => !item.hasAttribute("hidden")
        );
    }

    get selectedIndex() {
        return this.targetIndex;
    }

    select(index) {
        if (!(index in this.items)) return;

        const fromSlide = this.items[this.targetIndex];
        const toSlide = this.items[index];

        this.targetIndex = index;

        // Hide all items
        this.items.forEach(item => item.classList.remove("is-selected"));

        // Show selected item
        toSlide.classList.add("is-selected");

        // Dispatch change event
        this.dispatchEvent(new CustomEvent("carousel:change", {
            detail: { index, cell: toSlide }
        }));
    }
}

// Register custom elements
if (!window.customElements.get("carousel-navigation")) {
    window.customElements.define("carousel-navigation", CarouselNavigation);
}

if (!window.customElements.get("featured-collections-carousel")) {
    window.customElements.define("featured-collections-carousel", FeaturedCollectionsCarousel);
}

// Quick Add and Variant Selection functionality
document.addEventListener('DOMContentLoaded', () => {
    // Variant Selection
    const variantButtons = document.querySelectorAll('.variant-button');
    
    variantButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Get parent product card
            const productCard = button.closest('.product-card');
            const variantButtonsInCard = productCard.querySelectorAll('.variant-button');
            
            // Remove selected class from all variants in this card
            variantButtonsInCard.forEach(btn => btn.classList.remove('variant-button--selected'));
            
            // Add selected class to clicked button
            button.classList.add('variant-button--selected');
            
            // Update the add to cart button with new variant ID
            const addToCartBtn = productCard.querySelector('.product-card__add-to-cart');
            const variantId = button.dataset.variantId;
            if (addToCartBtn && variantId) {
                addToCartBtn.dataset.productId = variantId;
            }
        });
    });
    
    // Add to Cart functionality
    const addToCartButtons = document.querySelectorAll('.product-card__add-to-cart');
    
    addToCartButtons.forEach(button => {
        button.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            const variantId = button.dataset.productId;
            
            // Add loading state
            button.classList.add('loading');
            button.disabled = true;
            const originalText = button.textContent;
            button.textContent = 'ADDING...';
            
            try {
                // Add to cart via Shopify AJAX API
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        id: variantId,
                        quantity: 1
                    })
                });
                
                if (response.ok) {
                    button.textContent = 'ADDED!';
                    
                    // Trigger cart update event
                    document.dispatchEvent(new CustomEvent('cart:updated'));
                    
                    // Update cart icon bubble count using Section Rendering API
                    try {
                        const sectionsResponse = await fetch('/?sections=cart-icon-bubble');
                        if (sectionsResponse.ok) {
                            const sectionsData = await sectionsResponse.json();
                            const cartIconBubble = document.getElementById('cart-icon-bubble');
                            if (cartIconBubble && sectionsData['cart-icon-bubble']) {
                                const parser = new DOMParser();
                                const doc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
                                const newContent = doc.querySelector('.shopify-section');
                                if (newContent) {
                                    cartIconBubble.innerHTML = newContent.innerHTML;
                                }
                            }
                        }
                    } catch (err) {
                        console.error('Error updating cart icon bubble:', err);
                    }
                    
                    // Trigger cart drawer if it exists
                    const cartDrawer = document.querySelector('cart-drawer');
                    if (cartDrawer && typeof cartDrawer.open === 'function') {
                        cartDrawer.open();
                    }
                    
                    // Reset button after delay
                    setTimeout(() => {
                        button.textContent = originalText;
                        button.classList.remove('loading');
                        button.disabled = false;
                    }, 2000);
                } else {
                    throw new Error('Failed to add to cart');
                }
            } catch (error) {
                console.error('Error adding to cart:', error);
                button.textContent = 'ERROR';
                
                setTimeout(() => {
                    button.textContent = originalText;
                    button.classList.remove('loading');
                    button.disabled = false;
                }, 2000);
            }
        });
    });
});
